import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react'
import { useAuth } from '../auth-context/AuthContext'
import {
  apiResponse,
  createDownloadLink,
  ResponseType,
  sanitizeFilename,
  showSnackbar,
} from '../../utils/Utils'
import { useFetchWithAuth } from '../../hooks/useFetchWithAuth'
import {
  DataType,
  Observations,
  ParameterType,
  StationInfo,
  StatisticType,
} from '../../lib/TimeseriesLib'
import { VariantType } from '../../lib/CommonLib'

interface TimeseriesViewContextType {
  fetchObservationsData: (
    startDate: string,
    endDate: string,
    selectedDataType: string,
    selectedStation: string[],
    selectedParameters: string[],
    setObservations: (val: any) => void
  ) => void
  exportObservationData: (
    startDate: string,
    endDate: string,
    selectedDataType: string,
    selectedStation: string[],
    selectedParameters: string[],
    isRawData?: boolean
  ) => void
  statisticTypeList: StatisticType[]
  dataTypeList: DataType[]
  fetchMonthlyStatisticData: (
    startDate: string,
    endDate: string,
    selectedDataType: string,
    selectedStation: string[],
    selectedParameters: string[],
    selectedStatisticType: number,
    setStatisticData: (val: any) => void
  ) => void
  exportMonthlyStatisticData: (
    startDate: string,
    endDate: string,
    selectedDataType: string,
    selectedStation: string[],
    selectedParameters: string[],
    selectedStatisticType: number
  ) => void
  stationInfo: any
  setStationInfo: (val: any) => void
  isFullscreen: boolean
  setIsFullscreen: (val: boolean) => void
  fetchAreaList: (val: string, setAreaList: (val: string[]) => void) => void
  fetchStationList: (
    dataType: string,
    area: string,
    setStationList: (val: string[]) => void
  ) => void
  fetchParameterList: (val: string[], setParameterList: (val: ParameterType[]) => void) => void
  fetchStatisticTypeList: (val: string) => void
  fetchDataTypeList: () => void
  fetchThresholdData: (
    startDate: string,
    endDate: string,
    dataType: string,
    stations: string,
    parameters: string,
    setThresholdData: (val: any) => void
  ) => void
}

const TimeseriesContext = createContext<TimeseriesViewContextType | undefined>(undefined)

export const TimeseriesViewProvider = ({ children }: { children: ReactNode }) => {
  const { baseUrl, user, setSnackbarOpen, setSnackbarMessage, setVariant, setLoading } = useAuth()
  const fetchWithAuth = useFetchWithAuth()
  const [statisticTypeList, setStatisticTypeList] = useState<StatisticType[]>([])
  const [dataTypeList, setDataTypeList] = useState<DataType[]>([])
  const [stationInfo, setStationInfo] = useState<StationInfo>({
    selectedPeriod: 'Last 24 Hours',
    selectedDataType: 'WaterQuality',
    selectedStation: [],
    selectedParameters: [],
    selectedArea: '',
  })
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false)

  const fetchObservationsData = async (
    startDate: string,
    endDate: string,
    selectedDataType: string,
    selectedStation: string[],
    selectedParameters: string[],
    setObservations: (val: Observations[]) => void
  ) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth('/api/Observations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate,
          endDate,
          dataType: selectedDataType,
          stations: selectedStation.join('|'),
          parameters: selectedParameters.join('|'),
        }),
      })
      const data = await apiResponse(response)
      setObservations(data)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      setObservations([])
    } finally {
      setLoading(false)
    }
  }

  const exportObservationData = async (
    startDate: string,
    endDate: string,
    selectedDataType: string,
    selectedStation: string[],
    selectedParameters: string[],
    isRawData: boolean = false
  ) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth(`/api/Observations/exportascsv?isRawData=${isRawData}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate: startDate,
          endDate: endDate,
          dataType: selectedDataType,
          stations: selectedStation.join('|'),
          parameters: selectedParameters.join('|'),
        }),
      })
      const blob = await apiResponse(response, ResponseType.blob)
      const contentDisposition = response.headers.get('content-disposition')
      let filename = 'Observations.zip'
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename\*?=(?:UTF-8''([^;]+)|\"([^\"]+)\")/)
        if (matches) {
          filename = decodeURIComponent(matches[1] || matches[2])
        }
      }
      filename = sanitizeFilename(filename)
      const downloadUrl = window.URL.createObjectURL(blob)
      createDownloadLink(downloadUrl, filename)
      window.URL.revokeObjectURL(downloadUrl)
      showSnackbar(
        'Observation data successfully downloaded',
        VariantType.success,
        setVariant,
        setSnackbarOpen,
        setSnackbarMessage
      )
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  const fetchMonthlyStatisticData = async (
    startDate: string,
    endDate: string,
    selectedDataType: string,
    selectedStation: string[],
    selectedParameters: string[],
    selectedStatisticType: number,
    setStatisticData: (val: Observations[]) => void
  ) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth('/api/MonthlyStatistics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate: startDate,
          endDate: endDate,
          dataType: selectedDataType,
          stations: selectedStation.join('|'),
          parameters: selectedParameters.join('|'),
          statisticType: selectedStatisticType,
        }),
      })
      const data = await apiResponse(response)
      setStatisticData(data)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      setStatisticData([])
    } finally {
      setLoading(false)
    }
  }

  const fetchDataTypeList = async () => {
    try {
      setLoading(true)
      const response = await fetchWithAuth('/api/Observations/datatypes', {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const data = await apiResponse(response)
      setDataTypeList(data)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      setDataTypeList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (baseUrl && user?.token && dataTypeList.length === 0) {
      fetchDataTypeList()
    }
  }, [baseUrl, user?.token])

  const fetchStatisticTypeList = async (dataType: string) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth(
        `/api/MonthlyStatistics/StatisticTypes?dataType=${dataType}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      const data = await apiResponse(response)
      setStatisticTypeList(data)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      setStatisticTypeList([])
    } finally {
      setLoading(false)
    }
  }

  const fetchAreaList = async (dataType: string, setAreaList: (val: string[]) => void) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth(`/api/Observations/areas?datatype=${dataType}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const data = await apiResponse(response)
      setAreaList(data)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      setAreaList([])
    } finally {
      setLoading(false)
    }
  }

  const fetchStationList = async (
    dataType: string,
    area: string,
    setStationList: (val: string[]) => void
  ) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth(
        `/api/Observations/stations?datatype=${dataType}&area=${area}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      const data = await apiResponse(response)
      setStationList(data)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      setStationList([])
    } finally {
      setLoading(false)
    }
  }

  const fetchParameterList = async (
    stations: string[],
    setParameterList: (val: ParameterType[]) => void
  ) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth('/api/Observations/parameters', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(stations),
      })
      const data = await apiResponse(response)
      setParameterList(data)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      setParameterList([])
    } finally {
      setLoading(false)
    }
  }

  const exportMonthlyStatisticData = async (
    startDate: string,
    endDate: string,
    selectedDataType: string,
    selectedStation: string[],
    selectedParameters: string[],
    selectedStatisticType: number
  ) => {
    try {
      setLoading(true)
      const response = await fetchWithAuth('/api/MonthlyStatistics/exportascsv', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate: startDate,
          endDate: endDate,
          dataType: selectedDataType,
          stations: selectedStation.join('|'),
          parameters: selectedParameters.join('|'),
          statisticType: selectedStatisticType,
        }),
      })
      const blob = await apiResponse(response, ResponseType.blob)
      const contentDisposition = response.headers.get('content-disposition')
      let filename = 'Monthly_statistics.zip'
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename\*?=(?:UTF-8''([^;]+)|\"([^\"]+)\")/)
        if (matches) {
          filename = decodeURIComponent(matches[1] || matches[2])
        }
      }
      filename = sanitizeFilename(filename)
      const downloadUrl = window.URL.createObjectURL(blob)
      createDownloadLink(downloadUrl, filename)
      window.URL.revokeObjectURL(downloadUrl)
      showSnackbar(
        'Monthly statistic data successfully downloaded',
        VariantType.success,
        setVariant,
        setSnackbarOpen,
        setSnackbarMessage
      )
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
    } finally {
      setLoading(false)
    }
  }

  const fetchThresholdData = async (
    startDate: string,
    endDate: string,
    dataType: string,
    stations: string,
    parameters: string,
    setThresholdData: (val: any) => void
  ) => {
    try {
      setLoading(true)
      const requestBody = {
        startDate,
        endDate,
        dataType,
        stations,
        parameters,
      }
      const response = await fetchWithAuth('/api/Observations/thresholds', {
        method: 'POST',
        headers: {
          accept: '*/*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      })
      const data = await apiResponse(response)
      setThresholdData(data)
    } catch (error) {
      showSnackbar(`${error}`, VariantType.error, setVariant, setSnackbarOpen, setSnackbarMessage)
      setThresholdData([])
    } finally {
      setLoading(false)
    }
  }

  return (
    <TimeseriesContext.Provider
      value={{
        fetchObservationsData,
        exportObservationData,
        statisticTypeList,
        dataTypeList,
        fetchMonthlyStatisticData,
        exportMonthlyStatisticData,
        stationInfo,
        setStationInfo,
        isFullscreen,
        setIsFullscreen,
        fetchAreaList,
        fetchStationList,
        fetchParameterList,
        fetchStatisticTypeList,
        fetchDataTypeList,
        fetchThresholdData,
      }}
    >
      {children}
    </TimeseriesContext.Provider>
  )
}

export const useTimeseriesView = () => {
  const context = useContext(TimeseriesContext)
  if (context === undefined) {
    throw new Error('useTimeseriesView must be used within a TimeseriesViewProvider')
  }
  return context
}
